import React, { useEffect, useRef, useMemo, Suspense } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { useGLTF, useTexture, Sphere, ContactShadows, Html, useProgress } from "@react-three/drei";
import * as THREE from "three";

import autoBetGlobe from "../../../../../static/three_assets/models/autobet-globe/autobet.glb";
import baseTexture from "../../../../../static/three_assets/shaders/base.jpg";
import goldMatcap from "../../../../../static/three_assets/shaders/gold_shader_material.jpg";
import earthTexture from "../../../../../static/three_assets/textures/earth/earthImage.jpg";
import earthNormalMap from "../../../../../static/three_assets/textures/earth/earthImage_normals.jpg";
import earthRoughnessMap from "../../../../../static/three_assets/textures/earth/earthImage_Roughness.jpg";
import eightBallTexture from "../../../../../static/three_assets/textures/eight_ball/eightBall1.jpg";
import eightBall2Texture from "../../../../../static/three_assets/textures/eight_ball/eightBall2.jpg";

class WebGLErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasWebGLCrash: false };
  }

  static getDerivedStateFromError(error) {
    return { hasWebGLCrash: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("AutoBet 3D Engine Offline: VRAM Exhausted.", error);
  }

  render() {
    if (this.state.hasWebGLCrash) {
      return (
        <div className="flex flex-col items-center justify-center w-full h-full min-h-[320px] bg-[#050505] rounded-2xl border border-gray-800/50 shadow-2xl p-6">
          <div className="text-blue-500 mb-4 animate-pulse">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-white text-lg font-bold tracking-wider mb-2">GPU SYNC LOST</h3>
          <p className="text-gray-400 text-sm mb-6 text-center max-w-xs">
            Background rendering was suspended to save system memory.
          </p>
          <button 
            onClick={() => this.setState({ hasWebGLCrash: false })}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-all duration-300 shadow-[0_0_15px_rgba(37,99,235,0.5)]"
          >
            REBOOT 3D ENGINE
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function CanvasLoader() {
  const { progress } = useProgress();
  return (
    <Html center>
      <div className="flex flex-col items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
        <span className="text-blue-600 font-bold tracking-widest text-xs">
          {progress.toFixed(0)}%
        </span>
      </div>
    </Html>
  );
}

function VisibilityOptimizer() {
  const set = useThree((state) => state.set);
  
  useEffect(() => {
    const handleVisibility = () => {
      set({ frameloop: document.hidden ? "never" : "always" });
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [set]);

  return null;
}



function ResponsiveCamera() {
  const { camera, size } = useThree();

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      
      // Default desktop
      if (w >= 1200) {
        camera.zoom = 1;
        camera.setViewOffset(size.width, size.height, -size.width * 0.25, 0, size.width, size.height);
      } else if (w >= 1000) {
        camera.zoom = 0.8;
        camera.setViewOffset(size.width, size.height, -size.width * 0.28, 0, size.width, size.height);
      } else if (w >= 768) {
        camera.zoom = 0.65;
        camera.setViewOffset(size.width, size.height, -size.width * 0.32, 0, size.width, size.height);
      } else {
        camera.zoom = 0.5;
        camera.setViewOffset(size.width, size.height, -size.width * 0.35, 0, size.width, size.height);
      }
      
      camera.updateProjectionMatrix();
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [camera, size]);

  return null;
}

function Model() {
  const { scene: gltfScene } = useGLTF(autoBetGlobe);
  const scene = useMemo(() => gltfScene.clone(), [gltfScene]);
  const { gl } = useThree();

  const globeRef = useRef(null);
  const poolBallsRef = useRef([]);
  const coinsRef = useRef([]);
  const dragState = useRef(null);

  const textures = useTexture({
    baseColor: earthTexture,
    normal: earthNormalMap,
    roughness: earthRoughnessMap,
    eightBall1: eightBallTexture,
    eightBall2: eightBall2Texture,
    gold: goldMatcap,
    alpha: baseTexture,
  });

  const sharedMaterials = useMemo(() => {
    const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
    
    Object.values(textures).forEach((tex) => {
      tex.flipY = true; 
      tex.anisotropy = Math.min(4, maxAnisotropy); 
    });

    textures.baseColor.colorSpace = THREE.SRGBColorSpace;
    textures.eightBall1.colorSpace = THREE.SRGBColorSpace;
    textures.eightBall2.colorSpace = THREE.SRGBColorSpace;
    textures.gold.colorSpace = THREE.SRGBColorSpace;
    textures.normal.colorSpace = THREE.NoColorSpace;
    textures.roughness.colorSpace = THREE.NoColorSpace;

    return {
      eightBall1: new THREE.MeshPhysicalMaterial({
        map: textures.eightBall1, roughness: 0.1, metalness: 0.1, clearcoat: 0.6, envMapIntensity: 2.0
      }),
      eightBall2: new THREE.MeshPhysicalMaterial({
        map: textures.eightBall2, roughness: 0.1, metalness: 0.1, clearcoat: 0.6, envMapIntensity: 2.0
      }),
      base: new THREE.MeshPhysicalMaterial({
        color: new THREE.Color("#050505"), metalness: 0.8, roughness: 0.2, clearcoat: 0.3, envMapIntensity: 1.5
      }),
      gold: new THREE.MeshMatcapMaterial({ matcap: textures.gold }),
      glow: new THREE.MeshStandardMaterial({
        color: 0xffffff, emissive: "#2563EB", emissiveIntensity: 6.0, toneMapped: false
      })
    };
  }, [textures, gl]);

  useEffect(() => {
    poolBallsRef.current = [];
    coinsRef.current = [];
    
    let sharedBallGeo = null;
    const dynamicMaterials = [];

    scene.traverse((child) => {
      if (!child.isMesh) return;
      const meshName = child.name.toLowerCase();

      if (meshName.includes("coin")) {
        coinsRef.current.push(child);
        child.userData = {
          ...child.userData,
          isDraggable: true,
          rotSpeedX: (Math.random() - 0.5) * 0.4,
          rotSpeedY: (Math.random() - 0.5) * 0.4,
          rotSpeedZ: (Math.random() - 0.5) * 0.4
        };

        const oldMat = child.material;
        const newCoinMat = new THREE.MeshPhysicalMaterial({
          map: oldMat.map || null,
          color: oldMat.color || new THREE.Color(0xffffff),
          roughness: 0.1,
          metalness: 0.3,
          clearcoat: 0.8,
          clearcoatRoughness: 0.1,
          envMapIntensity: 2.0,
        });
        if (oldMat.vertexColors) newCoinMat.vertexColors = true;
        
        child.material = newCoinMat;
        dynamicMaterials.push(newCoinMat);
      }

      if (meshName === "8poolball_1" || meshName === "8poolball_2") {
        poolBallsRef.current.push(child);
        
        if (meshName === "8poolball_2" && !child.userData.positionAdjusted) {
          child.position.y -= 2.5; 
          child.position.x += 1.5;
          child.position.z += 1.0;
          child.userData.positionAdjusted = true;
        }

        child.userData = {
          ...child.userData,
          isDraggable: true,
          rotSpeedX: (Math.random() - 0.5) * 3.5,
          rotSpeedY: (Math.random() - 0.5) * 3.5,
          rotSpeedZ: (Math.random() - 0.5) * 3.5
        };
        
        child.geometry.computeBoundingSphere();
        if (!sharedBallGeo) {
          sharedBallGeo = new THREE.SphereGeometry(child.geometry.boundingSphere.radius, 32, 32);
        }
        child.geometry = sharedBallGeo;
        child.material = meshName === "8poolball_2" ? sharedMaterials.eightBall2 : sharedMaterials.eightBall1;
        child.rotation.set(0, Math.PI / 2, 0);
      }

      if (meshName === "base_main" || meshName === "circle") {
        child.material = sharedMaterials.base;
      }

      if (meshName.includes("gold")) {
        child.material = sharedMaterials.gold;
      }

      if (meshName.includes("glow_base")) {
        child.material = sharedMaterials.glow;

        if (!child.userData.hasLight) {
          const glowLight = new THREE.PointLight("#2563EB", 120, 20); 
          glowLight.position.set(0, 1.5, 0); 
          
          child.add(glowLight);
          child.userData.hasLight = true;
        }
      }
    });

    return () => {
      dynamicMaterials.forEach(mat => mat.dispose());
      if (sharedBallGeo) sharedBallGeo.dispose();
    };
  }, [scene, sharedMaterials]);

  useFrame((state, delta) => {
    if (globeRef.current) globeRef.current.rotation.y += delta * 0.15;

    coinsRef.current.forEach((coin) => {
      if (dragState.current?.object !== coin) {
        coin.rotation.x += delta * coin.userData.rotSpeedX;
        coin.rotation.y += delta * coin.userData.rotSpeedY;
        coin.rotation.z += delta * coin.userData.rotSpeedZ;
      }
    });

    poolBallsRef.current.forEach((ball) => {
      if (dragState.current?.object !== ball) {
        ball.rotation.x += delta * ball.userData.rotSpeedX;
        ball.rotation.y += delta * ball.userData.rotSpeedY;
        ball.rotation.z += delta * ball.userData.rotSpeedZ;
      }
    });

    if (dragState.current) {
      const drag = dragState.current;
      drag.velX = THREE.MathUtils.lerp(drag.velX, 0, 0.08);
      drag.velY = THREE.MathUtils.lerp(drag.velY, 0, 0.08);

      drag.object.rotateX(drag.velX);
      drag.object.rotateY(drag.velY);

      if (!drag.isDragging && Math.abs(drag.velX) < 0.001 && Math.abs(drag.velY) < 0.001) {
        dragState.current = null;
      }
    }
  });

  return (
    <group position={[0, -2.5, 0]}>
      <primitive
        object={scene}
        dispose={null}
        onPointerDown={(e) => {
          if (!e.object.userData.isDraggable) return;
          e.stopPropagation();
          e.target.setPointerCapture(e.pointerId);
          dragState.current = {
            object: e.object,
            lastX: e.clientX,
            lastY: e.clientY,
            velX: 0,
            velY: 0,
            isDragging: true,
          };
          document.body.style.cursor = "grabbing";
        }}
        onPointerMove={(e) => {
          if (dragState.current?.isDragging && dragState.current.object === e.object) {
            const deltaX = e.clientX - dragState.current.lastX;
            const deltaY = e.clientY - dragState.current.lastY;
            dragState.current.velX += deltaY * 0.005;
            dragState.current.velY += deltaX * 0.005;
            dragState.current.lastX = e.clientX;
            dragState.current.lastY = e.clientY;
          }
        }}
        onPointerUp={(e) => {
          if (dragState.current) {
            e.target.releasePointerCapture(e.pointerId);
            dragState.current.isDragging = false;
            document.body.style.cursor = "auto";
          }
        }}
        onPointerOver={(e) => {
          if (e.object.userData.isDraggable) {
            document.body.style.cursor = "grab";
          }
        }}
        onPointerOut={() => {
          if (!dragState.current?.isDragging) {
            document.body.style.cursor = "auto";
          }
        }}
      />

      <Sphere
        ref={globeRef}
        args={[2.5, 64, 64]} 
        position={[0, 3.5, 0]}
        rotation={[0, Math.PI / 2, 0]}
      >
        <meshPhysicalMaterial
          map={textures.baseColor}
          normalMap={textures.normal}
          roughnessMap={textures.roughness}
          roughness={0.7}
          metalness={0.2}
          clearcoat={0.3}
          clearcoatRoughness={0.1}
          envMapIntensity={1.2}
        />
      </Sphere>

      <ContactShadows 
        position={[0, -2.6, 0]} 
        opacity={0.85} 
        scale={25} 
        blur={2.5} 
        far={10} 
        resolution={256} 
        color="#000000" 
      />
    </group>
  );
}

export default function AutoBetGlobe({ className, style }) {
  return (
    <div className={className} style={{ width: "100%", height: "100%", minHeight: 320, ...style }}>
      <WebGLErrorBoundary>
        <Canvas
          dpr={[1, 1.5]} 
          gl={{ 
            antialias: true, 
            alpha: true, 
            toneMapping: THREE.ACESFilmicToneMapping, 
            toneMappingExposure: 1,
            powerPreference: "high-performance",
            failIfMajorPerformanceCaveat: false
          }}
          camera={{ position: [17, 26, 7], fov: 13 }}
        >
          <ResponsiveCamera />
          <VisibilityOptimizer />
          <Suspense fallback={<CanvasLoader />}>
            <ambientLight intensity={0.4} />
            <directionalLight position={[10, 20, 10]} intensity={1.5} />
            <Model />
          </Suspense>
        </Canvas>
      </WebGLErrorBoundary>
    </div>
  );
}

useGLTF.preload(autoBetGlobe);